// src/app/errors/global-error-handler.ts
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { Analytics, logEvent } from '@fido/firebase/analytics';
import { injectAppStale } from './app-stale';

/**
 * Matches chunk load failures caused by stale assets after a deploy.
 *
 * After a new deploy, lazy-loaded chunk filenames change but a user's
 * already-loaded main bundle still references the old names. When the
 * browser requests an old chunk, Firebase Hosting's catch-all rewrite
 * serves index.html (text/html) instead of JavaScript, causing either:
 *   - "Failed to fetch dynamically imported module"
 *   - A chunk filename pattern like "chunk-XXXXX.js" in the error
 *
 * Pattern borrowed from the Angular team's own angular.dev error handler:
 * https://github.com/angular/angular/blob/main/adev/src/app/core/services/errors-handling/error-handler.ts
 */
const CHUNK_ERROR_PATTERNS = [
  /chunk-(.*?)\.js/,
  /Failed to fetch dynamically imported module/,
  /Failed to load module script/,
  /Loading chunk \d+ failed/,
];

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly analytics = inject(Analytics, { optional: true });
  private readonly appStale = injectAppStale();

  handleError(error: unknown): void {
    // Always log to console — matches Angular's default ErrorHandler behavior.
    // provideBrowserGlobalErrorListeners() calls e.preventDefault() which
    // suppresses the browser's default console output, so if we don't log
    // here, global errors become invisible.
    console.error('ERROR', error);

    const errorMessage = this.extractMessage(error);
    if (this.isChunkLoadError(errorMessage)) {
      if (!this.appStale()) {
        this.appStale.set(true);
        if (this.analytics) {
          logEvent(this.analytics, 'exception', {
            description: errorMessage,
            code: 'CHUNK_LOAD_ERROR',
            fatal: true,
          });
        }
      }
      return;
    }

    // Log to Firebase Analytics for monitoring
    if (this.analytics) {
      logEvent(this.analytics, 'exception', {
        description: errorMessage,
        code: this.extractCode(error),
        fatal: false,
      });
    }
  }

  private isChunkLoadError(message: string): boolean {
    const firstLine = message.split('\n')[0];
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(firstLine));
  }

  private extractMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    if (typeof error === 'object' && error && 'message' in error) {
      return String(error.message);
    }
    return 'Unknown error occurred';
  }

  private extractCode(error: unknown): string | undefined {
    if (typeof error === 'object' && error && 'code' in error) {
      return String(error.code);
    }
    return undefined;
  }
}
